import moment from "moment";
import "moment/locale/es";
moment.locale("es");

// PDF de apartado de un pedido (complemento Empresa.pdfApartadoPedido).
// Datos: pedidos/datosApartadoPedidoPdf. Pendiente = cantidad - max(apartado, vendido) - requerido
// (ya viene calculado del backend).

const formatoCantidad = (n) =>
  (Number(n) || 0).toLocaleString("es-MX", { maximumFractionDigits: 4 });

const apartadoPedidoPdf = (values) => {
  const empresa = values.data.empresa;
  const cliente = values.data.cliente || {};
  const logoBiwo = values.logoBiwo;
  const insumos = values.data.insumos || [];

  const columnasNumericas = [
    "cantidad",
    "vendido",
    "requerido",
    "apartadoAnterior",
    "apartadoAhora",
    "apartadoTotal",
    "pendiente",
  ];

  const totales = {};
  for (const col of columnasNumericas) {
    totales[col] = insumos.reduce((acc, it) => acc + (Number(it[col]) || 0), 0);
  }

  const celdaNumero = (valor, extra = {}) => ({
    text: formatoCantidad(valor),
    style: "textoTabla",
    alignment: "right",
    ...extra,
  });

  const conceptos = insumos.map((it) => {
    // Se resaltan los renglones a los que se les aparto algo en este movimiento
    const resaltar = it.apartadoAhora > 0 ? { bold: true } : {};
    return [
      { text: it.clave || "", style: "textoTabla", alignment: "center", ...resaltar },
      { text: it.descripcion || "", style: "textoTabla", alignment: "left", ...resaltar },
      celdaNumero(it.cantidad, resaltar),
      celdaNumero(it.vendido, resaltar),
      celdaNumero(it.requerido, resaltar),
      celdaNumero(it.apartadoAnterior, resaltar),
      celdaNumero(it.apartadoAhora, it.apartadoAhora > 0 ? { ...resaltar, fillColor: "#d9f2d9" } : resaltar),
      celdaNumero(it.apartadoTotal, resaltar),
      celdaNumero(it.pendiente, { bold: true, color: it.pendiente > 0 ? "#b00020" : "black" }),
    ];
  });

  const filaTotales = [
    { text: "TOTALES", style: "tituloTabla", colSpan: 2, alignment: "right" },
    {},
    ...columnasNumericas.map((col) => celdaNumero(totales[col], { bold: true, fillColor: "#cccccc" })),
  ];

  const nombreCliente = cliente.persona
    ? cliente.razonSocial || "-"
    : `${cliente.nombre || ""} ${cliente.apellidoPaterno || ""} ${cliente.apellidoMaterno || ""}`.trim() || "-";

  return {
    footer: function () {
      if (!logoBiwo?.base64Completa) return null;
      return {
        image: "logoBiwo",
        alignment: "center",
        width: 50,
        margin: [0, 0, 0, 10],
      };
    },
    content: [
      {
        style: "tableExample",
        table: {
          widths: ["18%", "52%", "30%"],
          body: [[
            empresa.logo?.base64Completa
              ? { border: [false, false, false, false], image: "logo", fit: [80, 80] }
              : { border: [false, false, false, false], text: "" },
            {
              border: [false, false, false, false],
              table: {
                widths: ["100%"],
                body: [
                  [{ text: empresa.nombre || "-", style: "tableHeader" }],
                  [{ text: `RFC: ${empresa.rfc || "-"}`, style: "textoChico", alignment: "center" }],
                  [{ text: `${empresa.calle || "-"} ${empresa.numeroCalle || "-"}`, style: "textoChico", alignment: "center" }],
                  [{
                    text: `${empresa.colonia?.nombre || ""}, C.P. ${empresa.colonia?.cp || ""}, ${empresa.municipio?.nombre || ""}, ${empresa.estado?.nombre || ""}`,
                    style: "textoChico",
                    alignment: "center",
                  }],
                  [{ text: "APARTADO DE INSUMOS - PEDIDO", style: "tableHeader" }],
                ],
              },
              layout: "noBorders",
            },
            {
              border: [false, false, false, false],
              table: {
                widths: ["100%"],
                body: [
                  [{ text: "Pedido:", style: "subheader", alignment: "center" }],
                  [{ text: values.data.folio || "-", style: "textoChico", alignment: "center" }],
                  [{ text: "Fecha:", style: "subheader", alignment: "center" }],
                  [{ text: moment().format("LL LT"), style: "textoChico", alignment: "center" }],
                ],
              },
              layout: "noBorders",
            },
          ]],
        },
      },
      {
        style: "tableExample",
        table: {
          widths: ["100%"],
          body: [[{
            table: {
              widths: ["15%", "35%", "15%", "35%"],
              body: [
                [
                  { text: "Cliente:", style: "subheader" },
                  { text: nombreCliente, style: "textoChico" },
                  { text: "RFC:", style: "subheader" },
                  { text: cliente.rfc || "-", style: "textoChico" },
                ],
                [
                  { text: "Correo:", style: "subheader" },
                  { text: cliente.correo || "-", style: "textoChico" },
                  { text: "Teléfono:", style: "subheader" },
                  { text: cliente.telefono?.toString() || "-", style: "textoChico" },
                ],
              ],
            },
            layout: "noBorders",
          }]],
        },
      },
      {
        style: "tableExample",
        table: {
          headerRows: 1,
          widths: ["10%", "27%", "9%", "9%", "9%", "9%", "9%", "9%", "9%"],
          body: [
            [
              { text: "Clave", style: "tituloTabla" },
              { text: "Descripción", style: "tituloTabla" },
              { text: "Cantidad", style: "tituloTabla" },
              { text: "Vendido", style: "tituloTabla" },
              { text: "Requerido", style: "tituloTabla" },
              { text: "Apartado Anterior", style: "tituloTabla" },
              { text: "Apartado Ahora", style: "tituloTabla" },
              { text: "Apartado Total", style: "tituloTabla" },
              { text: "Pendiente", style: "tituloTabla" },
            ],
            ...conceptos,
            filaTotales,
          ],
        },
        layout: {
          fillColor: (rowIndex) => (rowIndex % 2 === 0 ? "#e8e8e8" : "#ffffff"),
          hLineColor: () => "#adaaaa",
          vLineColor: () => "#adaaaa",
        },
      },
      {
        text: "Pendiente = Cantidad - (el mayor entre Apartado Total y Vendido) - Requerido. En negritas los renglones apartados en este movimiento.",
        style: "textoChico",
        italics: true,
        margin: [0, 2, 0, 0],
      },
      {
        style: "tableExample",
        margin: [0, 50, 0, 0],
        table: {
          widths: ["35%", "30%", "35%"],
          body: [[
            { text: "Entrega", style: "textoTabla", alignment: "center", border: [false, true, false, false] },
            { text: "", border: [false, false, false, false] },
            { text: "Recibe", style: "textoTabla", alignment: "center", border: [false, true, false, false] },
          ]],
        },
      },
    ],
    images: {
      ...(empresa.logo?.base64Completa ? { logo: empresa.logo.base64Completa } : {}),
      ...(logoBiwo?.base64Completa ? { logoBiwo: logoBiwo.base64Completa } : {}),
    },
    styles: {
      header: { fontSize: 9, bold: true },
      subheader: { fontSize: 8, bold: true },
      tableExample: { margin: [0, 5, 0, 5] },
      tableHeader: { bold: true, fontSize: 12, color: "black", alignment: "center" },
      textoChico: { fontSize: 8 },
      tituloTabla: { fontSize: 8, bold: true, fillColor: "#cccccc", alignment: "center" },
      textoTabla: { fontSize: 8 },
    },
    pageSize: "LETTER",
    pageOrientation: "landscape",
    pageMargins: [20, 20, 20, 30],
  };
};

export default apartadoPedidoPdf;
